import { useMemo, useState } from "react";

import type { FoundryV2OrganizationMemoryRecord } from "../api/foundryV2Client";

function statusTone(status: "ready" | "watch" | "blocked"): string {
  if (status === "ready") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "watch") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-red-200 bg-red-50 text-red-700";
}

type Props = {
  organizationMemory: FoundryV2OrganizationMemoryRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundryOrganizationMemoryPanel({
  organizationMemory,
  isLoading,
  error,
}: Props): JSX.Element {
  const [domainFamily, setDomainFamily] = useState<string>("");
  const [preferredStack, setPreferredStack] = useState<string>("");
  const [patternId, setPatternId] = useState<string>("");

  const memory = organizationMemory?.organizationMemory ?? null;

  const filtered = useMemo(() => {
    if (!memory) {
      return {
        sourceProjects: [],
        decisionMemories: [],
        patternMemories: [],
        failureLearnings: [],
        recommendations: [],
      };
    }

    const matches = (candidate: { domainFamily?: string | null; preferredStack?: string | null; patternIds?: string[] }) => {
      if (domainFamily && candidate.domainFamily !== domainFamily) return false;
      if (preferredStack && candidate.preferredStack !== preferredStack) return false;
      if (patternId && !(candidate.patternIds ?? []).includes(patternId)) return false;
      return true;
    };

    const sourceProjects = memory.sourceProjects.filter(matches);
    const projectIds = new Set(sourceProjects.map((project) => project.projectId));
    const decisionMemories = memory.decisionMemories.filter(
      (decision) => projectIds.has(decision.projectId) && matches(decision),
    );
    const patternMemories = memory.patternMemories.filter((pattern) => {
      if (patternId && pattern.patternId !== patternId) return false;
      return pattern.observedProjectIds.some((projectIdValue) => projectIds.has(projectIdValue));
    });
    const failureLearnings = memory.failureLearnings.filter(
      (learning) => projectIds.has(learning.projectId) && matches(learning),
    );
    const recommendations = memory.recommendations.filter((recommendation) => {
      if (domainFamily && recommendation.domainFamily !== domainFamily) return false;
      if (preferredStack && recommendation.preferredStack !== preferredStack) return false;
      if (patternId && !recommendation.patternIds.includes(patternId)) return false;
      return recommendation.sourceProjectIds.some((projectIdValue) => projectIds.has(projectIdValue));
    });

    return {
      sourceProjects,
      decisionMemories,
      patternMemories,
      failureLearnings,
      recommendations,
    };
  }, [domainFamily, memory, patternId, preferredStack]);

  const localStatus: "ready" | "watch" | "blocked" =
    !memory || filtered.sourceProjects.length === 0
      ? "blocked"
      : filtered.failureLearnings.some((learning) => learning.severity === "blocking")
        ? "watch"
        : "ready";

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#fbfdfd_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-organization-memory-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Organization Memory</div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">Foundry and Jarvis can reuse real decisions, patterns, and failures</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            This memory layer turns prior project truth into reusable guidance. Filter by domain, stack, or pattern to
            see which defaults are already proven and which failure modes must not be repeated.
          </p>
        </div>
        {memory ? (
          <div className="min-w-[220px] rounded-2xl border border-slate-200 bg-white/90 p-4">
            <div className="flex items-center gap-2">
              <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(localStatus)}`}>
                {localStatus}
              </span>
              <span className="text-xs text-slate-500">
                {filtered.sourceProjects.length} project(s) in scope
              </span>
            </div>
            <div className="mt-3 text-sm text-slate-700">{memory.summary.headline}</div>
            <a className="mt-3 block text-xs font-medium text-blue-700 underline" href={organizationMemory?.artifactUrls.organizationMemory}>
              organization_memory.v1.json
            </a>
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !memory ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading organization memory...
        </div>
      ) : null}

      {memory ? (
        <>
          <div className="mt-5 grid gap-3 lg:grid-cols-3">
            <label className="rounded-2xl border border-slate-200 bg-white/90 p-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Domain
              <select
                className="mt-3 block w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-normal tracking-normal text-slate-700"
                value={domainFamily}
                onChange={(event) => setDomainFamily(event.target.value)}
              >
                <option value="">All domains</option>
                {memory.query.availableDomainFamilies.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </select>
            </label>

            <label className="rounded-2xl border border-slate-200 bg-white/90 p-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Stack
              <select
                className="mt-3 block w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-normal tracking-normal text-slate-700"
                value={preferredStack}
                onChange={(event) => setPreferredStack(event.target.value)}
              >
                <option value="">All stacks</option>
                {memory.query.availablePreferredStacks.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </select>
            </label>

            <label className="rounded-2xl border border-slate-200 bg-white/90 p-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Pattern
              <select
                className="mt-3 block w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-normal tracking-normal text-slate-700"
                value={patternId}
                onChange={(event) => setPatternId(event.target.value)}
              >
                <option value="">All patterns</option>
                {memory.query.availablePatternIds.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Recommendations</div>
                <div className="text-xs text-slate-500">
                  {filtered.recommendations.length} active · {filtered.decisionMemories.length} prior decisions
                </div>
              </div>
              <div className="mt-3 space-y-3">
                {filtered.recommendations.map((recommendation) => (
                  <div key={recommendation.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">{recommendation.title}</div>
                      <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                        {(recommendation.confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-slate-700">{recommendation.recommendation}</div>
                    <ul className="mt-3 space-y-2 text-xs text-slate-600">
                      {recommendation.rationale.map((entry) => (
                        <li key={`${recommendation.id}-${entry}`} className="rounded-xl border border-white bg-white px-3 py-2">
                          {entry}
                        </li>
                      ))}
                    </ul>
                    {recommendation.patternIds.length ? (
                      <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
                        {recommendation.patternIds.map((entry) => (
                          <span key={`${recommendation.id}-${entry}`} className="rounded-full border border-slate-200 bg-white px-2 py-1">
                            {entry}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
                {!filtered.recommendations.length ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                    No memory-backed recommendation matches the active slice yet.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Source projects</div>
                <div className="mt-3 space-y-3 text-xs text-slate-600">
                  {filtered.sourceProjects.map((project) => (
                    <div key={project.projectId} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold text-slate-900">{project.appName}</div>
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${statusTone(project.readinessStatus)}`}>
                          {project.readinessStatus}
                        </span>
                      </div>
                      <div className="mt-2">{project.headline}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Failure learnings</div>
                <div className="mt-3 space-y-3 text-xs text-slate-600">
                  {filtered.failureLearnings.map((learning) => (
                    <div
                      key={learning.id}
                      className={`rounded-xl border px-3 py-3 ${
                        learning.severity === "blocking"
                          ? "border-red-200 bg-red-50 text-red-800"
                          : "border-amber-200 bg-amber-50 text-amber-800"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-semibold">{learning.title}</div>
                        <span className="rounded-full border border-current/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                          {learning.severity}
                        </span>
                      </div>
                      <div className="mt-2">{learning.summary}</div>
                    </div>
                  ))}
                  {!filtered.failureLearnings.length ? (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-emerald-800">
                      No recurring failure remains visible on this memory slice.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
